import {environment} from 'src/environments/environment';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { BehaviorSubject, Subject, from, of } from 'rxjs';
import { catchError, switchMap, tap, timeout } from 'rxjs/operators';
import { posSyncDb } from './pos-sync-db';

/** Which parts of the menu a modifier group assign / unassign applies to (ENG-5802). The lists are unioned. */
export interface ModGroupScope {
  menu_group_ids?: number[];
  menu_item_group_ids?: number[];
  menu_item_ids?: number[];
}

@Injectable()
export class MenuServiceV2 {
  endpoint = environment.apiUrl;
  menuEndpoint = `${this.endpoint}/backend/business/store/`;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
    })
  };

  public menuCategorySuggestionUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public updateSearchFilterMenuSetting: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public menuItemGroupEditUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** ENG-4644: Signal that the full menu sync is complete and pos-menu can load from cache. */
  private _menuReady = new BehaviorSubject<boolean>(false);
  public menuReady$ = this._menuReady.asObservable();

  /** Fires when background HTTP revalidation completes after Dexie served stale data.
   *  pos-menu should re-render to pick up any admin changes made while offline. */
  public menuRevalidated$ = new Subject<void>();

  /** Concurrency guard: if syncFullMenu is already running, callers await the in-flight promise. */
  private _syncInFlight: Promise<void> | null = null;

  constructor(private http: HttpClient){}

  updateMenuGroup(storeId:string, payload: string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group/update/`, payload, this.httpOptions);
  }

  deleteMenuGroup(storeId:string, payload: string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group/delete/`, payload, this.httpOptions);
  }

  getStoreMenuGroups(storeId: string) {
    return this.http.get<Object>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group/`, this.httpOptions);
  }

  getStoreMenuGroup(storeId: string, menuGroupId:string, menuAscDescOrder:string) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group/${menuGroupId}/`, {params: {dir: menuAscDescOrder}});
  }

  getStoreMenuGroupMenuItemGroup(storeId: string, menuGroupId:string, menuAscDescOrder:string) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group_menu_item_group/${menuGroupId}/`, {params: {dir: menuAscDescOrder}});
  }

  getStoreMenuItemGroup(storeId: string, menuItemGroupId) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/`, this.httpOptions);
  }

  updateStoreMenuItemGroup(storeId: string, menuItemGroupId:string, payload:string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/update/`, payload, this.httpOptions);
  }

  bulkUpdateStoreMenuItemGroups(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/bulk_update/`, payload, this.httpOptions);
  }

  /** Suggestions for the "these also have chicken" prompt after `source`
   *  was switched to `isAvailable`. */
  getRelatedAvailability(storeId: string | number, source: { type: string; id: number }, isAvailable: boolean) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/availability/related/`, {
      params: { source_type: source.type, source_id: String(source.id), is_available: String(isAvailable) }
    });
  }

  /** Menu items and modifier options whose name/portion/group contains `q`,
   *  with their availability and the time they come back (ENG availability search). */
  searchAvailability(storeId: string | number, q: string, availability?: 'all' | 'unavailable' | 'available' | string) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/availability/search/`, {
      params: { q: q || '', availability: availability || 'all' }
    });
  }

  /** Apply the operator's selection from that prompt (or a search-result
   *  selection) in one call. `reset` / `reset_time` say when the rows come
   *  back when switching them off (reset 7 = at the given instant). */
  bulkUpdateAvailability(storeId: string | number, payload: {
    is_available: boolean; menu_item_group_ids: number[]; modifier_option_ids: number[];
    reset?: number; reset_time?: string | null;
    source?: { type: string; id: number; name: string }; keywords?: string[];
  }) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/availability/bulk/`,
      JSON.stringify(payload), this.httpOptions);
  }

  /** One-click availability for a whole menu item (every portion at once). */
  updateMenuItemGroupAvailability(storeId: string | number, menuItemGroupId: string | number, isAvailable: boolean,
                                  when?: { reset?: number; reset_time?: string | null }) {
    return this.http.post<any>(
      this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/availability/`,
      JSON.stringify({ is_available: isAvailable, ...(when || {}) }), this.httpOptions);
  }

  addMenuItemGroup(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/add/`, payload, this.httpOptions);
  }

  deleteStoreMenuItemGroup(storeId: string, menuItemGroupId:string) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/delete/`, this.httpOptions);
  }

  getStoreMenuItemsV2(storeId: string) {
    return this.http.get(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/`, this.httpOptions);
  }

  addMenuItem(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/add/`, payload, this.httpOptions);
  }

  updateMenuItem(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/update/`, payload, this.httpOptions);
  }

  deleteMenuItem(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/delete/`, payload, this.httpOptions);
  }

  getModGroup(storeId:string){
    return this.http.get(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/`, this.httpOptions);
  }

  getModifierOptions(storeId: string, modGroupId: string, availability: string, searchTerm: string, modAscDescOrder: string) {
    const params: any = {
      availability: availability ?? '',
      search_term: searchTerm ?? '',
      dir: modAscDescOrder ?? ''
    };
    return this.http.get<any>(`${this.menuEndpoint}${storeId}/menu_management_v2/mod_group/${modGroupId}/`, { params });
}

  getModifierOption(storeId:string, modId:string){
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_option/${modId}/`);
  }


  addModGroup(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/add/`, payload, this.httpOptions);
  }

  updateModGroup(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/update/`, payload, this.httpOptions);
  }

  addModifier(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/modifier/add/`, payload, this.httpOptions);
  }

  updateModifier(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/modifier/update/`, payload, this.httpOptions);
  }

  deleteModGroup(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/delete/`, payload, this.httpOptions);
  }

  deleteModifier(storeId:string, payload:string) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/modifier/delete/`, payload, this.httpOptions);
  }

  /* ──────────────────────────────────────────────────────────────────
   *  Sub-Modifier Groups — attach modifier groups under a parent
   *  modifier option (e.g. "Iced Coffee" → "Milk Type" + "Sweetener").
   *  Endpoints are served by the DRF router mounted at /backend/v2/.
   * ──────────────────────────────────────────────────────────────────*/
  private masterMenuEndpoint = `${this.endpoint}/backend/v2/master-menu`;
  private subGroupEndpoint = `${this.masterMenuEndpoint}/modifier-option-sub-groups`;

  getSubModifierGroupMappings(storeId: number | string, modifierOptionId: number | string) {
    const params: any = { store: String(storeId), modifier_option: String(modifierOptionId) };
    return this.http.get<any[]>(`${this.subGroupEndpoint}/`, { params });
  }

  /** All sub-group mappings for a store in one call. Used to hydrate
   *  per-option counts and the local tree without N+1 per-option fetches. */
  getAllSubModifierGroupMappings(storeId: number | string) {
    const params: any = { store: String(storeId) };
    return this.http.get<any[]>(`${this.subGroupEndpoint}/`, { params });
  }

  bulkAssignSubModifierGroups(storeId: number | string, modifierOptionId: number | string, modifierGroupIds: number[]) {
    const payload = {
      store: Number(storeId),
      modifier_option: Number(modifierOptionId),
      modifier_group_ids: modifierGroupIds,
    };
    return this.http.post<any[]>(`${this.subGroupEndpoint}/bulk_assign/`, payload, this.httpOptions);
  }

  deleteSubModifierGroupMapping(mappingId: number | string) {
    return this.http.delete(`${this.subGroupEndpoint}/${mappingId}/`);
  }

  /** Where a modifier group is used — top-level (on menu items) and as a
   *  sub-group (under modifier options). Powers the "Used in N places"
   *  badge and the multi-use delete confirmation. */
  getModifierGroupUsages(modifierGroupId: number | string) {
    return this.http.get<any>(`${this.masterMenuEndpoint}/modifier-groups/${modifierGroupId}/usages/`);
  }

  getMenuItemModifiers(storeId: string, payload: string) {
    return this.http.get<any>(this.menuEndpoint.replace('/business', '') + `${storeId}/menu_item/${payload}/modifiers/`);
  }

  getModGroupForMenuItem(storeId:string, payload:string){
    return this.http.post<any[]>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/mod_group/`, payload, this.httpOptions);
  }

  updateModGroupForMenuItem(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/mod_group/update/`, payload, this.httpOptions);
  }

  updateIndexModGroupForMenuItem(storeId:string, payload:any){
    return this.http.put(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/mod_group_update_index/`, payload, this.httpOptions);
  }
  
  deleteModGroupForMenuItem(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item/mod_group/delete/`, payload, this.httpOptions);
  }

  updateOrder(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/order/`, payload, this.httpOptions);
  }

  deleteImage(storeId, menuGroupId){
    return this.http.get(`${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_item_group/${menuGroupId}/image/delete/`)
  }

  deleteSingleImage(storeId, menuGroupId, imageID){
    return this.http.get(`${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_item_group/${menuGroupId}/${imageID}/image/delete/`)
  }

  /**
   * ENG-4644: Cache TTL in ms.  Set to 8 hours (one shift) because MQTT
   * invalidation handles real-time menu changes — the TTL is only a fallback
   * if an MQTT event is missed (reconnect gap).  The old 2-minute TTL caused
   * HTTP calls to menu_items and modifiers during every order.
   */
  private static MENU_CACHE_TTL = 8 * 60 * 60 * 1000;

  /**
   * ENG-4644: 3-layer cache for full menu:
   *   L1 — in-memory Map (fastest, synchronous)
   *   L2 — Dexie IndexedDB (survives reload, unlimited size)
   *   L3 — HTTP (network)
   */
  private _menuGroupsCache = new Map<string, { ts: number; data: any }>();

  getStoreMenuLazy(storeId, filter) {
    const sid = String(storeId); // normalize — callers pass number or string
    if (!filter) {
      // L1: In-memory
      const memCached = this._menuGroupsCache.get(sid);
      if (memCached && (Date.now() - memCached.ts) < MenuServiceV2.MENU_CACHE_TTL) {
        return of(memCached.data);
      }

      // L2: Dexie IndexedDB (async)
      return from(posSyncDb.menu_cache.get(sid).catch(() => undefined)).pipe(
        switchMap(dexieCached => {
          if (dexieCached && (Date.now() - dexieCached.ts) < MenuServiceV2.MENU_CACHE_TTL) {
            // Promote to L1
            this._menuGroupsCache.set(sid, { ts: dexieCached.ts, data: dexieCached.data });
            return of(dexieCached.data);
          }
          // L3: HTTP fetch → write to both L1 + L2
          return this.http.get(`${environment.apiUrl}/backend/store/${sid}/lazy_menu_item_groups/`,
            {params: {filter: '', is_pos: 'true'}}).pipe(
            tap((data) => {
              const ts = Date.now();
              this._menuGroupsCache.set(sid, { ts, data });
              posSyncDb.menu_cache.put({ storeId: sid, data, ts }).catch(() => {});
            })
          );
        })
      );
    }

    return this.http.get(`${environment.apiUrl}/backend/store/${sid}/lazy_menu_item_groups/`,
      {params: {filter: filter || '', is_pos: 'true'}});
  }

  /**
   * ENG-4644: Single-call POS menu sync — fetches groups + items + modifiers
   * in one HTTP call from /pos_menu_sync/ and populates all L1 + L2 caches.
   *
   * Architecture (non-blocking):
   *   Phase 1 — L1 (in-memory Maps): populate synchronously → emit menuReady.
   *             POS menu can render immediately from L1.
   *   Phase 2 — L2 (Dexie/IndexedDB): persist in background using bulkPut
   *             (single transaction) with chunked yields so the main thread
   *             stays responsive. This is fire-and-forget; failures are logged
   *             but don't block the user.
   *
   * On page reload, Dexie caches are checked first — if all fresh, zero HTTP.
   */
  async syncFullMenu(storeId: string): Promise<void> {
    console.log(`[MenuV2] syncFullMenu called for store=${storeId}, inFlight=${!!this._syncInFlight}`);
    // Concurrency guard: if a sync is already running, await it instead of starting a second one
    if (this._syncInFlight) {
      return this._syncInFlight;
    }
    this._syncInFlight = this._doSyncFullMenu(storeId);
    try {
      await this._syncInFlight;
    } finally {
      this._syncInFlight = null;
    }
  }

  private async _doSyncFullMenu(storeId: string): Promise<void> {
    const sid = String(storeId);

    let dexieServed = false;

    // ── Dexie freshness check — serve instantly from L2 on reload ──
    try {
      const [menuCached, itemsSample, modsSample] = await Promise.all([
        posSyncDb.menu_cache.get(sid).catch(() => undefined),
        posSyncDb.items_cache.where('storeId').equals(sid).first().catch(() => undefined),
        posSyncDb.modifiers_cache.where('storeId').equals(sid).first().catch(() => undefined),
      ]);
      const now = Date.now();
      const hasComplete = menuCached && menuCached.complete === true;
      const menuFresh = menuCached && (now - menuCached.ts) < MenuServiceV2.MENU_CACHE_TTL;
      const itemsFresh = itemsSample && (now - itemsSample.ts) < MenuServiceV2.ITEM_CACHE_TTL;
      const modsFresh = modsSample && (now - modsSample.ts) < MenuServiceV2.ITEM_CACHE_TTL;
      if (hasComplete && menuFresh && itemsFresh && modsFresh) {
        // Promote Dexie → L1 for instant display
        this._menuGroupsCache.set(sid, { ts: menuCached.ts, data: menuCached.data });
        const allItems = await posSyncDb.items_cache.where('storeId').equals(sid).toArray();
        for (const entry of allItems) {
          this._itemsCache.set(`${sid}:${entry.groupId}`, { ts: entry.ts, data: entry.data });
        }
        const allMods = await posSyncDb.modifiers_cache.where('storeId').equals(sid).toArray();
        for (const entry of allMods) {
          this._modifiersCache.set(`${sid}:${entry.itemId}`, { ts: entry.ts, data: entry.data });
        }
        const dexieGroupIds = allItems.map(e => e.groupId);
        console.log(`[MenuV2] Dexie served store=${sid}: ${allItems.length} groups, has 2011240=${dexieGroupIds.includes('2011240')}`);
        this._menuReady.next(true);
        dexieServed = true;
      }
    } catch (e) {
      // Dexie unavailable — proceed to HTTP
    }

    // ── HTTP call — initial fetch or background revalidation ──
    try {
      const resp: any = await this.http.get(
        `${environment.apiUrl}/backend/store/${sid}/pos_menu_sync/`
      ).pipe(timeout(45000)).toPromise();
      const ts = Date.now();

      if (!resp || !resp.items_by_group) {
        if (!dexieServed) this._menuReady.next(true);
        return;
      }

      // ── Phase 1: L1 memory cache (fast, non-blocking) ──

      // 1a. Menu groups
      this._menuGroupsCache.set(sid, { ts, data: resp.menu_groups });

      // 1b. Items per group
      const itemKeys = Object.keys(resp.items_by_group);
      console.log(`[MenuV2] pos_menu_sync store=${sid}: ${itemKeys.length} groups cached:`, itemKeys);
      for (const gid of itemKeys) {
        this._itemsCache.set(`${sid}:${gid}`, { ts, data: resp.items_by_group[gid] });
      }

      // 1c. Build item-by-id lookup
      const itemById: Record<string, any> = {};
      for (const gid of itemKeys) {
        for (const item of resp.items_by_group[gid]) {
          itemById[String(item.id)] = item;
        }
      }

      // 1d. Reconstruct per-item modifier data from deduplicated response.
      //     Backend sends each modifier group ONCE; we expand per-item here.
      const allModGroups = resp.all_modifier_groups || {};
      const itemModMap = resp.item_modifier_map || {};
      const preselected = resp.preselected || {};
      const modDisplayHv = resp.modifier_display_hv || false;
      const allItemIds = Object.keys(itemById);

      for (const iid of allItemIds) {
        const groupIds: number[] = itemModMap[iid] || [];
        const groups: any[] = [];
        for (const gid of groupIds) {
          const srcGroup = allModGroups[String(gid)];
          if (!srcGroup || !srcGroup.options || !srcGroup.options.length) continue;
          const gd: any = { ...srcGroup };
          const itemPs = (preselected[iid] || {})[String(gid)] || {};
          gd.options = srcGroup.options.map((opt: any) => ({
            ...opt,
            preselect_default: itemPs[String(opt.id)]?.is_preselected ?? false,
            preselect_quantity: itemPs[String(opt.id)]?.quantity ?? 1,
          }));
          groups.push(gd);
        }
        this._modifiersCache.set(`${sid}:${iid}`, {
          ts,
          data: {
            menu_item: itemById[iid] || {},
            modifier_groups: groups,
            modifier_display_hv: modDisplayHv,
          },
        });
      }

      // ── Menu is usable — signal ready BEFORE slow Dexie writes ──
      if (resp.menu_version) this.setMenuVersion(sid, resp.menu_version);
      this._menuReady.next(true);

      // If Dexie served stale data first, tell pos-menu to re-render with fresh HTTP data.
      if (dexieServed) {
        this.menuRevalidated$.next();
      }

      // ── Phase 2: Persist to Dexie in background (fire-and-forget) ──
      this._persistMenuToDexie(sid, ts, resp, allItemIds);

    } catch (err: any) {
      if (!dexieServed) this._menuReady.next(true);
    }
  }

  /**
   * Persist menu data to Dexie (IndexedDB) in the background.
   * Uses bulkPut (single IDB transaction per table) instead of individual put()
   * calls, and chunks modifier writes with setTimeout(0) yields to keep the
   * main thread responsive.
   */
  private async _persistMenuToDexie(
    sid: string, ts: number, resp: any, allItemIds: string[]
  ): Promise<void> {
    try {
      // Menu groups — single record
      await posSyncDb.menu_cache.put({ storeId: sid, data: resp.menu_groups, ts, complete: true });

      // Items — one bulkPut transaction for all groups
      const itemKeys = Object.keys(resp.items_by_group);
      const itemRows = itemKeys.map(gid => ({
        storeId: sid, groupId: gid, data: resp.items_by_group[gid], ts,
      }));
      if (itemRows.length) {
        await posSyncDb.items_cache.bulkPut(itemRows);
      }

      // Modifiers — chunked bulkPut, yield between chunks to avoid jank
      const CHUNK = 200;
      for (let i = 0; i < allItemIds.length; i += CHUNK) {
        const chunk = allItemIds.slice(i, i + CHUNK);
        const modRows = chunk.map(iid => ({
          storeId: sid,
          itemId: iid,
          data: this._modifiersCache.get(`${sid}:${iid}`)?.data || {},
          ts,
        }));
        await posSyncDb.modifiers_cache.bulkPut(modRows);
        // Yield to main thread between chunks
        if (i + CHUNK < allItemIds.length) {
          await new Promise<void>(r => setTimeout(r, 0));
        }
      }
    } catch (e) {
      // Dexie persist failed — non-fatal, menu still usable from L1
    }
  }

  /** ENG-4644: In-memory L1 cache for items per group and modifiers per item. */
  private _itemsCache = new Map<string, { ts: number; data: any }>();
  private _modifiersCache = new Map<string, { ts: number; data: any }>();
  private static ITEM_CACHE_TTL = 8 * 60 * 60 * 1000; // 8 hours — MQTT invalidates on changes
  /** G-2: Track menu versions per store. */
  private _menuVersions = new Map<string, number>();

  /** ENG-4644: Clear all cached menu data for a store (in-memory + Dexie). */
  clearMenuCache(storeId: string) {
    const sid = String(storeId);
    // L1: Clear in-memory caches
    this._menuGroupsCache.delete(sid);
    this._itemsCache.forEach((_, key) => {
      if (key.startsWith(`${sid}:`)) this._itemsCache.delete(key);
    });
    this._modifiersCache.forEach((_, key) => {
      if (key.startsWith(`${sid}:`)) this._modifiersCache.delete(key);
    });
    // G-2: Clear version tracking for this store
    this._menuVersions.delete(sid);
    // L2: Clear Dexie caches
    posSyncDb.menu_cache.delete(sid).catch(() => {});
    posSyncDb.items_cache.where('storeId').equals(sid).delete().catch(() => {});
    posSyncDb.modifiers_cache.where('storeId').equals(sid).delete().catch(() => {});
    // Legacy: remove localStorage entry if still present
    try { localStorage.removeItem(`pos_menu_cache_${sid}`); } catch (e) {}
    // Reset menu ready signal — syncFullMenu will re-emit when cache is repopulated
    this._menuReady.next(false);
  }

  /** G-2: Store the latest menu version received via MQTT. */
  setMenuVersion(storeId: string, version: number) {
    this._menuVersions.set(storeId, version);
  }

  /** G-2: Get the latest known menu version for a store. */
  getMenuVersion(storeId: string): number {
    return this._menuVersions.get(storeId) || 0;
  }

  /** G-3: Check current menu version on server for catch-up sync. */
  checkMenuVersion(storeId: string) {
    return this.http.get<{version: number}>(`${environment.apiUrl}/backend/store/${storeId}/menu/version/`);
  }

  /** G-1: Apply granular availability changes to ALL cache layers.
   *
   *  Backend now sends menu_item_group_id in each item so we can directly
   *  map MenuItem changes to their MenuItemGroup in _menuGroupsCache.
   *
   *  Steps:
   *    1. Patch _itemsCache — update is_available/available_qty in place (keep items visible).
   *    2. Track which MenuItemGroups have ALL items unavailable (sold out).
   *    3. Mark/unmark _soldOut on MenuItemGroups in _menuGroupsCache.
   *
   *  Returns true if any MenuItemGroups changed sold-out status (callers
   *  should trigger a menu re-render to update the grid).
   */
  updateItemsAvailability(storeId: string, items: Array<{id: number; is_available: boolean; available_qty: number; menu_item_group_id?: number}>): boolean {
    const sid = String(storeId);
    const changedById = new Map(items.map(i => [i.id, i]));
    const anyBecameUnavailable = items.some(i => !i.is_available);

    // Track which MenuItemGroups have ALL items unavailable (sold out)
    // and which were restocked (some items became available again).
    const soldOutGroupIds = new Set<string>();
    const restockedGroupIds = new Set<string>();
    const affectedGroupIds = new Set<string>();
    for (const item of items) {
      if (item.menu_item_group_id != null) {
        affectedGroupIds.add(String(item.menu_item_group_id));
      }
    }

    // 1. Patch _itemsCache — update in place instead of filtering out
    this._itemsCache.forEach((entry, key) => {
      if (!key.startsWith(`${sid}:`)) return;
      if (!Array.isArray(entry.data)) return;
      const groupId = key.substring(sid.length + 1);

      let modified = false;
      entry.data.forEach((mi: any) => {
        const change = changedById.get(mi.id);
        if (!change) return;
        mi.is_available = change.is_available;
        mi.available_qty = change.available_qty;
        modified = true;
      });

      if (modified) {
        const allUnavailable = entry.data.every((mi: any) => mi.is_available === false);
        if (allUnavailable) {
          soldOutGroupIds.add(groupId);
        } else {
          restockedGroupIds.add(groupId);
        }
        const now = Date.now();
        entry.ts = now;
        posSyncDb.items_cache.put({ storeId: sid, groupId, data: entry.data, ts: now }).catch(() => {});
      }
    });

    // Handle groups not yet loaded in _itemsCache — use payload data
    if (affectedGroupIds.size > 0) {
      for (const gid of affectedGroupIds) {
        if (this._itemsCache.has(`${sid}:${gid}`)) continue;
        const groupItems = items.filter(i => String(i.menu_item_group_id) === gid);
        if (groupItems.length > 0 && groupItems.every(i => !i.is_available)) {
          soldOutGroupIds.add(gid);
        } else if (groupItems.length > 0 && groupItems.some(i => i.is_available)) {
          restockedGroupIds.add(gid);
        }
      }
    }

    // 2. Mark/unmark _soldOut on MenuItemGroups in _menuGroupsCache
    //    _menuGroupsCache has: menu_items[].list[].list[] where innermost
    //    items are MenuItemGroups (with .id = MenuItemGroup ID).
    let anyStatusChanged = false;
    const menuEntry = this._menuGroupsCache.get(sid);
    if (menuEntry && menuEntry.data && (soldOutGroupIds.size > 0 || restockedGroupIds.size > 0)) {
      const menuItems = menuEntry.data.menu_items || menuEntry.data;
      if (Array.isArray(menuItems)) {
        for (const group of menuItems) {
          if (!group.list || !Array.isArray(group.list)) continue;
          for (const subGroup of group.list) {
            if (!subGroup.list || !Array.isArray(subGroup.list)) continue;
            for (const mig of subGroup.list) {
              const migId = String(mig.id);
              if (soldOutGroupIds.has(migId) && !mig._soldOut) {
                mig._soldOut = true;
                anyStatusChanged = true;
              } else if (restockedGroupIds.has(migId) && mig._soldOut) {
                mig._soldOut = false;
                anyStatusChanged = true;
              }
            }
          }
        }
      }
      const now = Date.now();
      menuEntry.ts = now;
      posSyncDb.menu_cache.put({ storeId: sid, data: menuEntry.data, ts: now, complete: true }).catch(() => {});
    }

    // 3. Clean modifier cache for items that became unavailable
    if (anyBecameUnavailable) {
      const unavailableIds = new Set(items.filter(i => !i.is_available).map(i => String(i.id)));
      this._modifiersCache.forEach((_, key) => {
        if (!key.startsWith(`${sid}:`)) return;
        const itemId = key.substring(sid.length + 1);
        if (unavailableIds.has(itemId)) this._modifiersCache.delete(key);
      });
    }

    return anyStatusChanged;
  }

  /** Look up available_qty for a MenuItem from L1 cache. Returns null if not tracked or not cached. */
  getItemAvailableQty(storeId: string, menuItemId: number): number | null {
    const sid = String(storeId);
    for (const [key, entry] of this._itemsCache.entries()) {
      if (!key.startsWith(`${sid}:`)) continue;
      if (!Array.isArray(entry.data)) continue;
      const item = entry.data.find((mi: any) => mi.id === menuItemId);
      if (item) return item.available_qty ?? null;
    }
    return null;
  }

  /**
   * G-1 ext: Apply a single menu item delta from admin edit MQTT push.
   * Patches the item in-place in both _itemsCache and _menuGroupsCache
   * so the UI updates immediately without an HTTP re-fetch.
   */
  applyMenuItemDelta(storeId: string, itemId: number, changes: Record<string, any>, action: string) {
    const sid = String(storeId);

    // _menuGroupsCache has MenuItemGroup IDs (not MenuItem IDs), so we
    // patch _itemsCache first (correct IDs), then update _menuGroupsCache
    // by removing MenuItemGroups that lost all their items.

    // 1. Patch _itemsCache (has actual MenuItems with correct IDs)
    const emptyGroupIds = new Set<string>();
    if (action === 'deleted') {
      this._itemsCache.forEach((entry, key) => {
        if (!key.startsWith(`${sid}:`)) return;
        if (!Array.isArray(entry.data)) return;
        const groupId = key.substring(sid.length + 1);
        const before = entry.data.length;
        entry.data = entry.data.filter((mi: any) => mi.id !== itemId);
        if (entry.data.length < before) {
          if (entry.data.length === 0) {
            this._itemsCache.delete(key);
            emptyGroupIds.add(groupId);
            posSyncDb.items_cache.delete([sid, groupId]).catch(() => {});
          } else {
            const now = Date.now();
            entry.ts = now;
            posSyncDb.items_cache.put({ storeId: sid, groupId, data: entry.data, ts: now }).catch(() => {});
          }
        }
      });
    } else {
      this._itemsCache.forEach((entry, key) => {
        if (!key.startsWith(`${sid}:`)) return;
        if (!Array.isArray(entry.data)) return;
        const groupId = key.substring(sid.length + 1);
        for (const mi of entry.data) {
          if (mi.id === itemId) {
            Object.assign(mi, changes);
            const now = Date.now();
            entry.ts = now;
            posSyncDb.items_cache.put({ storeId: sid, groupId, data: entry.data, ts: now }).catch(() => {});
            break;
          }
        }
      });
    }

    // 2. Remove empty MenuItemGroups from _menuGroupsCache (on delete only)
    if (action === 'deleted' && emptyGroupIds.size > 0) {
      const menuEntry = this._menuGroupsCache.get(sid);
      if (menuEntry && menuEntry.data) {
        const menuItems = menuEntry.data.menu_items || menuEntry.data;
        if (Array.isArray(menuItems)) {
          for (const group of menuItems) {
            if (!group.list || !Array.isArray(group.list)) continue;
            for (const subGroup of group.list) {
              if (!subGroup.list || !Array.isArray(subGroup.list)) continue;
              subGroup.list = subGroup.list.filter((mig: any) => !emptyGroupIds.has(String(mig.id)));
            }
            group.list = group.list.filter((sg: any) => sg.list && sg.list.length > 0);
          }
        }
        const now = Date.now();
        menuEntry.ts = now;
        posSyncDb.menu_cache.put({ storeId: sid, data: menuEntry.data, ts: now, complete: true }).catch(() => {});
      }
    }
  }

  /**
   * G-1 ext: Apply a single modifier delta from admin edit MQTT push.
   * Patches the modifier in-place in _modifiersCache so the UI updates
   * immediately without an HTTP re-fetch.
   */
  applyModifierDelta(storeId: string, modifierId: number, changes: Record<string, any>, action: string) {
    const sid = String(storeId);

    this._modifiersCache.forEach((entry, key) => {
      if (!key.startsWith(`${sid}:`)) return;
      if (!entry.data || !Array.isArray(entry.data.modifier_groups)) return;
      for (const group of entry.data.modifier_groups) {
        if (!Array.isArray(group.options)) continue;
        if (action === 'deleted') {
          group.options = group.options.filter((opt: any) => opt.id !== modifierId);
        } else {
          for (const opt of group.options) {
            if (opt.id === modifierId) {
              Object.assign(opt, changes);
              break;
            }
          }
        }
      }
      // Persist patched entry to Dexie with fresh timestamp
      const itemId = key.substring(sid.length + 1);
      const now = Date.now();
      entry.ts = now;
      posSyncDb.modifiers_cache.put({ storeId: sid, itemId, data: entry.data, ts: now }).catch(() => {});
    });
  }

  /**
   * ENG-4644: Batch-apply menu item changes from a single MQTT message.
   * More efficient than calling applyMenuItemDelta per-item: iterates
   * each cache entry only once and does one Dexie persist per group.
   */
  /**
   * @returns true when items were missing from cache (caller must trigger full sync).
   */
  applyMenuItemsBatch(storeId: string, items: Array<Record<string, any>>, action: string, menuItemGroup?: Record<string, any>): boolean {
    const sid = String(storeId);
    const changesMap = new Map<number, Record<string, any>>();
    for (const item of items) {
      changesMap.set(item.id, item);
    }

    if (action === 'deleted') {
      const emptyGroupIds = new Set<string>();
      this._itemsCache.forEach((entry, key) => {
        if (!key.startsWith(`${sid}:`)) return;
        if (!Array.isArray(entry.data)) return;
        const groupId = key.substring(sid.length + 1);
        const before = entry.data.length;
        entry.data = entry.data.filter((mi: any) => !changesMap.has(mi.id));
        if (entry.data.length < before) {
          if (entry.data.length === 0) {
            this._itemsCache.delete(key);
            emptyGroupIds.add(groupId);
            posSyncDb.items_cache.delete([sid, groupId]).catch(() => {});
          } else {
            const now = Date.now();
            entry.ts = now;
            posSyncDb.items_cache.put({ storeId: sid, groupId, data: entry.data, ts: now }).catch(() => {});
          }
        }
      });
      if (emptyGroupIds.size > 0) {
        const menuEntry = this._menuGroupsCache.get(sid);
        if (menuEntry && menuEntry.data) {
          const menuItems = menuEntry.data.menu_items || menuEntry.data;
          if (Array.isArray(menuItems)) {
            for (const group of menuItems) {
              if (!group.list || !Array.isArray(group.list)) continue;
              for (const subGroup of group.list) {
                if (!subGroup.list || !Array.isArray(subGroup.list)) continue;
                subGroup.list = subGroup.list.filter((mig: any) => !emptyGroupIds.has(String(mig.id)));
              }
              group.list = group.list.filter((sg: any) => sg.list && sg.list.length > 0);
            }
          }
          const now = Date.now();
          menuEntry.ts = now;
          posSyncDb.menu_cache.put({ storeId: sid, data: menuEntry.data, ts: now, complete: true }).catch(() => {});
        }
      }
      return false;
    }

    // Update: single pass over items cache, patch all matching items.
    // Track which batch IDs were actually found so we can detect missing items.
    const patchedIds = new Set<number>();
    const affectedGroupIds = new Set<string>();

    this._itemsCache.forEach((entry, key) => {
      if (!key.startsWith(`${sid}:`)) return;
      if (!Array.isArray(entry.data)) return;
      const groupId = key.substring(sid.length + 1);
      let changed = false;
      for (const mi of entry.data) {
        const delta = changesMap.get(mi.id);
        if (delta) {
          Object.assign(mi, delta);
          patchedIds.add(mi.id);
          affectedGroupIds.add(groupId);
          changed = true;
        }
      }
      if (changed) {
        const now = Date.now();
        entry.ts = now;
        posSyncDb.items_cache.put({ storeId: sid, groupId, data: entry.data, ts: now }).catch(() => {});
      }
    });

    // If any items in the batch weren't found in the cache (e.g. previously
    // removed when marked unavailable), signal the caller to do a full sync
    // so the POS gets the complete, current menu.
    if (patchedIds.size < changesMap.size) {
      return true;
    }

    // Update _soldOut flags on affected MenuItemGroups so items that
    // became available again are no longer greyed out on the POS.
    if (affectedGroupIds.size > 0) {
      const menuEntry = this._menuGroupsCache.get(sid);
      if (menuEntry && menuEntry.data) {
        const menuItems = menuEntry.data.menu_items || menuEntry.data;
        if (Array.isArray(menuItems)) {
          for (const group of menuItems) {
            if (!group.list || !Array.isArray(group.list)) continue;
            for (const subGroup of group.list) {
              if (!subGroup.list || !Array.isArray(subGroup.list)) continue;
              for (const mig of subGroup.list) {
                if (!affectedGroupIds.has(String(mig.id))) continue;
                const cacheKey = `${sid}:${mig.id}`;
                const itemsEntry = this._itemsCache.get(cacheKey);
                if (itemsEntry && Array.isArray(itemsEntry.data)) {
                  const allUnavailable = itemsEntry.data.every((mi: any) => !mi.is_available);
                  mig._soldOut = allUnavailable;
                }
              }
            }
          }
        }
      }
    }

    // Also patch the MenuItemGroup entry in L1 (starting_price, colors)
    if (menuItemGroup && menuItemGroup.id) {
      const menuEntry = this._menuGroupsCache.get(sid);
      if (menuEntry && menuEntry.data) {
        const menuItems = menuEntry.data.menu_items || menuEntry.data;
        if (Array.isArray(menuItems)) {
          for (const group of menuItems) {
            if (!group.list || !Array.isArray(group.list)) continue;
            for (const subGroup of group.list) {
              if (!subGroup.list || !Array.isArray(subGroup.list)) continue;
              for (const mig of subGroup.list) {
                if (mig.id === menuItemGroup.id) {
                  if (menuItemGroup.starting_price != null) mig.starting_price = menuItemGroup.starting_price;
                  if (menuItemGroup.item_color != null) mig.item_color = menuItemGroup.item_color;
                  if (menuItemGroup.item_text_color != null) mig.item_text_color = menuItemGroup.item_text_color;
                }
              }
            }
          }
        }
      }
    }

    // Persist menu groups cache with updated _soldOut flags + metadata
    if (affectedGroupIds.size > 0 || (menuItemGroup && menuItemGroup.id)) {
      const menuEntry = this._menuGroupsCache.get(sid);
      if (menuEntry) {
        const now = Date.now();
        menuEntry.ts = now;
        posSyncDb.menu_cache.put({ storeId: sid, data: menuEntry.data, ts: now, complete: true }).catch(() => {});
      }
    }
    return false;
  }

  /**
   * ENG-4644: Batch-apply modifier changes from a single MQTT message.
   * Iterates each modifier cache entry once and does one Dexie persist per item.
   */
  applyModifiersBatch(storeId: string, modifiers: Array<Record<string, any>>, action: string, groupInfo?: Record<string, any>) {
    const sid = String(storeId);
    const changesMap = new Map<number, Record<string, any>>();
    for (const mod of modifiers) {
      changesMap.set(mod.id, mod);
    }

    this._modifiersCache.forEach((entry, key) => {
      if (!key.startsWith(`${sid}:`)) return;
      if (!entry.data || !Array.isArray(entry.data.modifier_groups)) return;
      let changed = false;
      for (const group of entry.data.modifier_groups) {
        // Patch group-level fields (e.g. enable_pre_mod, pre_mod_tags, max_mods)
        if (groupInfo && groupInfo.id === group.id) {
          const { id, ...groupFields } = groupInfo;
          Object.assign(group, groupFields);
          changed = true;
        }
        if (!Array.isArray(group.options)) continue;
        if (action === 'deleted') {
          const before = group.options.length;
          group.options = group.options.filter((opt: any) => !changesMap.has(opt.id));
          if (group.options.length < before) changed = true;
        } else {
          for (const opt of group.options) {
            const delta = changesMap.get(opt.id);
            if (delta) {
              Object.assign(opt, delta);
              changed = true;
            }
          }
        }
      }
      if (changed) {
        const itemId = key.substring(sid.length + 1);
        const now = Date.now();
        entry.ts = now;
        posSyncDb.modifiers_cache.put({ storeId: sid, itemId, data: entry.data, ts: now }).catch(() => {});
      }
    });
  }

  getMenuItemsForMenuGroup(storeId, menuGroupId) {
    const sid = String(storeId);
    const gid = String(menuGroupId);
    const cacheKey = `${sid}:${gid}`;

    // L1: In-memory
    const memCached = this._itemsCache.get(cacheKey);
    if (memCached && (Date.now() - memCached.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
      return of(memCached.data);
    }

    // L2: Dexie → L3: HTTP (or wait for in-flight syncFullMenu)
    return from(posSyncDb.items_cache.get([sid, gid]).catch(() => undefined)).pipe(
      switchMap(dexieCached => {
        if (dexieCached && (Date.now() - dexieCached.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
          this._itemsCache.set(cacheKey, { ts: dexieCached.ts, data: dexieCached.data });
          return of(dexieCached.data);
        }
        // ENG-4644: If a bulk pos_menu_sync is already in-flight, wait for it
        // to populate L1 instead of firing a per-group HTTP request.
        // This prevents redundant individual API calls while the full menu
        // sync is catching up (e.g. slow pre-prod network on first load).
        if (this._syncInFlight) {
          return from(this._syncInFlight).pipe(
            switchMap(() => {
              const fresh = this._itemsCache.get(cacheKey);
              if (fresh) return of(fresh.data);
              // Rare: group not included in pos_menu_sync — fall back to HTTP
              console.warn(`[MenuV2] group ${gid} not in pos_menu_sync for store ${sid} (after sync) — HTTP fallback`);
              return this.http.get<any[]>(`${environment.apiUrl}/backend/store/${sid}/menu_item_groups/${menuGroupId}/menu_items/`,
                {params: {is_pos: 'true'}}).pipe(
                tap((data) => {
                  const ts = Date.now();
                  this._itemsCache.set(cacheKey, { ts, data });
                  posSyncDb.items_cache.put({ storeId: sid, groupId: gid, data, ts }).catch(() => {});
                })
              );
            })
          );
        }
        // Re-check L1 before HTTP: handles race where syncFullMenu completed between
        // the initial synchronous L1 check (above) and this async switchMap callback.
        const lateL1 = this._itemsCache.get(cacheKey);
        if (lateL1 && (Date.now() - lateL1.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
          return of(lateL1.data);
        }
        console.warn(`[MenuV2] group ${gid} not in cache for store ${sid} (no sync in-flight) — HTTP fallback`);
        return this.http.get<any[]>(`${environment.apiUrl}/backend/store/${sid}/menu_item_groups/${menuGroupId}/menu_items/`,
          {params: {is_pos: 'true'}}).pipe(
          tap((data) => {
            const ts = Date.now();
            this._itemsCache.set(cacheKey, { ts, data });
            posSyncDb.items_cache.put({ storeId: sid, groupId: gid, data, ts }).catch(() => {});
          })
        );
      })
    );
  }

  getModifiersForMenuItem(storeId, menuItemId) {
    const sid = String(storeId);
    const iid = String(menuItemId);
    const cacheKey = `${sid}:${iid}`;

    // L1: In-memory
    const memCached = this._modifiersCache.get(cacheKey);
    if (memCached && (Date.now() - memCached.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
      return of(memCached.data);
    }

    // L2: Dexie → L3: HTTP (or wait for in-flight syncFullMenu)
    return from(posSyncDb.modifiers_cache.get([sid, iid]).catch(() => undefined)).pipe(
      switchMap(dexieCached => {
        if (dexieCached && (Date.now() - dexieCached.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
          this._modifiersCache.set(cacheKey, { ts: dexieCached.ts, data: dexieCached.data });
          return of(dexieCached.data);
        }
        // ENG-4644: Wait for in-flight syncFullMenu instead of per-item HTTP request.
        if (this._syncInFlight) {
          return from(this._syncInFlight).pipe(
            switchMap(() => {
              const fresh = this._modifiersCache.get(cacheKey);
              if (fresh) return of(fresh.data);
              // Rare: item not in pos_menu_sync response — fall back to HTTP
              return this.http.get(`${environment.apiUrl}/backend/store/${sid}/menu_item/${menuItemId}/modifiers/`).pipe(
                tap((data) => {
                  const ts = Date.now();
                  this._modifiersCache.set(cacheKey, { ts, data });
                  posSyncDb.modifiers_cache.put({ storeId: sid, itemId: iid, data, ts }).catch(() => {});
                })
              );
            })
          );
        }
        // Re-check L1 before HTTP: handles race where syncFullMenu completed between
        // the initial synchronous L1 check (above) and this async switchMap callback.
        const lateL1 = this._modifiersCache.get(cacheKey);
        if (lateL1 && (Date.now() - lateL1.ts) < MenuServiceV2.ITEM_CACHE_TTL) {
          return of(lateL1.data);
        }
        return this.http.get(`${environment.apiUrl}/backend/store/${sid}/menu_item/${menuItemId}/modifiers/`).pipe(
          tap((data) => {
            const ts = Date.now();
            this._modifiersCache.set(cacheKey, { ts, data });
            posSyncDb.modifiers_cache.put({ storeId: sid, itemId: iid, data, ts }).catch(() => {});
          })
        );
      })
    );
  }

  getMenuCategoryAvailability(storeId, menuGroupName){
    return this.http.get(`${environment.apiUrl}/backend/v2/menu/availability/${storeId}/`, {params: {menu_category_name: menuGroupName} });
  }

  updateMenuCategoryAvailability(payload){
    return this.http.post(`${environment.apiUrl}/backend/v2/menu/availability/`, payload, this.httpOptions);
  }

  searchMenuItem(storeId, searchTerm, isAvailable){
    return this.http.get(`${environment.apiUrl}/backend/v2/menu/search/`, {params: {store_id: storeId,
      search_term:searchTerm, availability: isAvailable} });
  }

  searchModifiers(storeId, searchTerm, isAvailable){
    return this.http.get(`${environment.apiUrl}/backend/v2/menu/mod/search/`, {params: {store_id: storeId,
      search_term:searchTerm, availability: isAvailable} });
  }

  createMenuGroupCopy(storeId:any, id:any) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group_copy/${id}/`, this.httpOptions);
  }

  createModifierCopy(storeId:any, mod_id:any) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group_copy/${mod_id}/`, this.httpOptions);
  }

  menuImageUpload(storeId:any, menuGroupId:any, payload:any) {
    return this.http.post(`${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_group/${menuGroupId}/image_resized/`, payload);
  }
  menuImageDelete(storeId:any, menuGroupId:any, type:any) {
    return this.http.get(`${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/menu_group/${menuGroupId}/${type}/image/delete/`);
  }

  getGenerateBarcode(storeId, menuId){
    return this.http.get(`${environment.apiUrl}/backend/business/store/${storeId}/menu_management_v2/generate_barcode/${menuId}/`, { responseType: 'blob' });
  }
  
  createMenuCatCopy(storeId:any, id:any) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group_copy/${id}/`, this.httpOptions);
  }

  createModCopy(storeId:any, mod_id:any) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/mod_copy/${mod_id}/`, this.httpOptions);
  }

  getMenuIconList() {
    return this.http.get<Object>(`${this.endpoint}/backend/` + `store/get_menu_icon_list/`, this.httpOptions);
  }

  getModOptionIds(storeId: string) {
    return this.http.get<Object>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_option_id_list/`, this.httpOptions);
  }

  getMenuItemIds(storeId: string) {
    return this.http.get<Object>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_id_list/`, this.httpOptions);
  }

  updateBulkModGroupForMenuItems(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/bulk_update_mod/`, payload, this.httpOptions);
  }

  // ── ENG-5802: modifier-group-first usage view + bulk assign ──────────────

  /** Every modifier group of the current menu with the categories / items / portions it is assigned to. */
  getModGroupUsage(storeId: string | number) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/usage/`, this.httpOptions);
  }

  /** The whole live menu (category → item → portion) flagged with whether each portion carries the group. */
  getModGroupUsageDetail(storeId: string | number, modGroupId: number) {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/${modGroupId}/usage/`, this.httpOptions);
  }

  /** Assign the group to every live portion under the given categories / items / portions. */
  assignModGroup(storeId: string | number, modGroupId: number, scope: ModGroupScope) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/${modGroupId}/assign/`, scope, this.httpOptions);
  }

  /** Remove the group from every live portion under the given categories / items / portions. */
  unassignModGroup(storeId: string | number, modGroupId: number, scope: ModGroupScope) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/mod_group/${modGroupId}/unassign/`, scope, this.httpOptions);
  }

  addModifierToMultipleModGroup(storeId:string, payload:string){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/modifier/multi_add/`, payload, this.httpOptions);
  }

  sortCompletMenuModItems(storeId: String, sort:String, key:String) {
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/${sort}/${key}/sort_menu/`, this.httpOptions);
  }

  getModOptionsList(storeId:string, menu_group_id: any, menu_item_id: any){
    return this.http.get(this.menuEndpoint + `${storeId}/menu_management_v2/mod_options/${menu_group_id}/${menu_item_id}/`, this.httpOptions);
  }

  savePreselectedMod(storeId:string, payload:any){
    return this.http.post(this.menuEndpoint + `${storeId}/menu_management_v2/preselected_mod/`, payload, this.httpOptions);
  }

  getMenuGroupMenuItemGroup(storeId: string, menuGroupId:string, menuAscDescOrder:string = '') {
    return this.http.get<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_group_menu_item_group/pos/${menuGroupId}/`, {params: {dir: menuAscDescOrder}});
  }

  // ENG-4454: Single Tap Add Menu Item to Order

  /** List all active single-tap configs for the store (used by Orders tab on load) */
  getSingleTapConfigs(storeId: string) {
    return this.http.get<any[]>(this.menuEndpoint + `${storeId}/menu_management_v2/single-tap/`, this.httpOptions);
  }

  /** List all single-tap configs for a specific MenuItemGroup */
  getSingleTapConfigsForItem(storeId: string, menuItemGroupId: number) {
    return this.http.get<any[]>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/single-tap/`, this.httpOptions);
  }

  /** Create a NEW single-tap config for a MenuItemGroup */
  createSingleTapConfig(storeId: string, menuItemGroupId: number, payload: { portion: number; custom_name?: string; modifiers?: number[]; modifier_quantities?: { [modId: number]: number }; is_active?: boolean }) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/single-tap/create/`, payload, this.httpOptions);
  }

  /** Update an existing single-tap config by ID */
  updateSingleTapConfig(storeId: string, menuItemGroupId: number, configId: number, payload: { portion?: number; custom_name?: string; modifiers?: number[]; modifier_quantities?: { [modId: number]: number }; is_active?: boolean }) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/single-tap/${configId}/update/`, payload, this.httpOptions);
  }

  /** Toggle is_active on a specific config */
  toggleSingleTapConfig(storeId: string, menuItemGroupId: number, configId: number, isActive?: boolean) {
    const body = isActive !== undefined ? { is_active: isActive } : {};
    return this.http.patch<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/single-tap/${configId}/toggle/`, body, this.httpOptions);
  }

  /** Delete a specific single-tap config */
  deleteSingleTapConfig(storeId: string, menuItemGroupId: number, configId: number) {
    return this.http.delete<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/${menuItemGroupId}/single-tap/${configId}/delete/`, this.httpOptions);
  }

  // ── Subcategory Management ──────────────────────────────────────────

  createEmptySubcategory(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/subcategory/create/`, payload, this.httpOptions);
  }

  reassignMenuItemGroup(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/menu_item_group/reassign/`, payload, this.httpOptions);
  }

  // ── Master Category CRUD ───────────────────────────────────────────

  getMasterCategories(storeId: string) {
    return this.http.get<any[]>(this.menuEndpoint + `${storeId}/menu_management_v2/master_category/`, this.httpOptions);
  }

  addMasterCategory(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/master_category/add/`, payload, this.httpOptions);
  }

  updateMasterCategory(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/master_category/update/`, payload, this.httpOptions);
  }

  deleteMasterCategory(storeId: string, payload: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/master_category/delete/`, payload, this.httpOptions);
  }

  translateKitchenNames(storeId: string) {
    return this.http.post<any>(this.menuEndpoint + `${storeId}/menu_management_v2/translate_kitchen_names/`, '{}', this.httpOptions);
  }
}
